import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, Clock, AlertTriangle, Check, Phone, Home, Target, Shield, Rocket, Building2, GraduationCap, Store, FileText, Star } from "lucide-react";
import HalaAvatar, { AVATAR_STATES } from "../components/HalaAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONT_FAMILY = "'Tajawal', 'Segoe UI', sans-serif";

const COLORS = {
  primary: '#1D8F4E',
  primaryLight: '#E8F5EE',
  primaryDark: '#167A42',
  secondary: '#1B3A6B',
  gold: '#C5A572',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgLight: '#FAFAFA',
  bgGray: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
};

const STATUS_CONFIG = {
  NOT_STARTED: { label: 'لم يبدأ', color: COLORS.textLight, bgColor: COLORS.bgGray, icon: Clock },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bgColor: '#FEF3C7', icon: Rocket },
  COMPLETED: { label: 'مكتمل', color: COLORS.primary, bgColor: COLORS.primaryLight, icon: Check },
};

export default function PlanReviewPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('search');
  const [mobileNumber, setMobileNumber] = useState('');
  const [planData, setPlanData] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateMobile = (m) => /^05\d{8}$/.test(m.trim());

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!mobileNumber.trim()) {
      setError('يرجى إدخال رقم الجوال');
      return;
    }
    if (!validateMobile(mobileNumber)) {
      setError('رقم الجوال غير صحيح. يجب أن يبدأ بـ 05 ويكون 10 أرقام');
      return;
    }

    setIsLoading(true);
    setStep('loading');

    try {
      const res = await axios.post(`${API}/plans/retrieve`, {
        mobile_number: mobileNumber.trim(),
      });

      if (res.data.success && res.data.plan) {
        setPlanData(res.data.plan);
        setStep('result');
      } else if (res.data.expired) {
        setStep('expired');
      } else {
        setStep('not_found');
      }
    } catch (err) {
      console.error('Plan retrieve error:', err);
      if (err.response?.status === 404 || err.response?.data?.found === false) {
        setStep('not_found');
      } else if (err.response?.status === 410 || err.response?.data?.expired) {
        setStep('expired');
      } else {
        setError('حدث خطأ. يرجى المحاولة مرة أخرى.');
        setStep('search');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleContinuePlan = () => {
    navigate(`/?mobile_number=${encodeURIComponent(mobileNumber.trim())}`);
  };

  const handleRetry = () => {
    setStep('search');
    setPlanData(null);
    setError('');
  };

  // ── SEARCH STEP ──
  if (step === 'search') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_FAMILY }} dir="rtl">
        <div style={{ backgroundColor: 'white', borderRadius: '24px', padding: '40px', maxWidth: '420px', width: '100%', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <HalaAvatar state={AVATAR_STATES.IDLE} size={80} showStatusDot={false} style={{ margin: '0 auto 16px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '8px', fontFamily: FONT_FAMILY }}>
              راجع خطتك
            </h1>
            <p style={{ fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6, fontFamily: FONT_FAMILY }}>
              أدخل رقم جوالك لعرض خطة الدعم الخاصة بك
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSearch}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.text, marginBottom: '8px', fontFamily: FONT_FAMILY }}>
                رقم الجوال
              </label>
              <div style={{ position: 'relative' }}>
                <Phone style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: COLORS.textLight }} />
                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder="05XXXXXXXX"
                  maxLength={10}
                  data-testid="mobile-number-input"
                  style={{
                    width: '100%',
                    padding: '16px 44px 16px 14px',
                    fontSize: '16px',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '12px',
                    outline: 'none',
                    fontFamily: FONT_FAMILY,
                    backgroundColor: COLORS.bgLight,
                    boxSizing: 'border-box',
                    direction: 'ltr',
                    textAlign: 'right',
                  }}
                />
              </div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '8px', fontFamily: FONT_FAMILY }}>
                الخطة صالحة لمدة 7 أيام من تاريخ إنشائها
              </p>
            </div>

            {error && (
              <div style={{ backgroundColor: COLORS.errorLight, border: `1px solid ${COLORS.error}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle style={{ width: '18px', height: '18px', color: COLORS.error }} />
                <span style={{ fontSize: '13px', color: COLORS.error, fontFamily: FONT_FAMILY }}>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              data-testid="search-plan-btn"
              style={{
                width: '100%', padding: '16px', backgroundColor: COLORS.primary, color: 'white',
                borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: 'bold',
                cursor: isLoading ? 'default' : 'pointer', fontFamily: FONT_FAMILY,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <Search style={{ width: '20px', height: '20px' }} />
              {isLoading ? 'جاري البحث...' : 'عرض خطتي'}
            </button>
          </form>

          <button
            onClick={handleBackToHome}
            data-testid="back-to-home-btn"
            style={{
              width: '100%', padding: '14px', backgroundColor: 'transparent', color: COLORS.textMuted,
              borderRadius: '12px', border: `1px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px', marginTop: '16px',
            }}
          >
            <Home style={{ width: '18px', height: '18px' }} />
            العودة للصفحة الرئيسية
          </button>
        </div>
      </div>
    );
  }

  // ── LOADING STEP ──
  if (step === 'loading') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_FAMILY }} dir="rtl">
        <HalaAvatar state={AVATAR_STATES.THINKING} size={100} showStatusDot={false} />
        <h2 style={{ fontSize: '20px', color: COLORS.secondary, marginTop: '24px', fontFamily: FONT_FAMILY }}>
          جاري البحث عن خطتك...
        </h2>
      </div>
    );
  }

  // ── NOT FOUND STEP ──
  if (step === 'not_found') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_FAMILY }} dir="rtl">
        <div style={{ backgroundColor: 'white', borderRadius: '24px', padding: '40px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: COLORS.bgGray, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <Search style={{ width: '40px', height: '40px', color: COLORS.textMuted }} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '12px', fontFamily: FONT_FAMILY }}>
            لا توجد خطة مرتبطة بهذا الرقم
          </h2>
          <p style={{ fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6, marginBottom: '24px', fontFamily: FONT_FAMILY }}>
            لم نجد خطة دعم مرتبطة برقم الجوال المُدخل. تأكد من صحة الرقم أو ابدأ محادثة جديدة مع هلا.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={handleRetry} data-testid="retry-search-btn" style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: COLORS.secondary, borderRadius: '12px', border: `1px solid ${COLORS.border}`, fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: FONT_FAMILY }}>
              حاول مرة أخرى
            </button>
            <button onClick={handleBackToHome} data-testid="start-new-chat-btn" style={{ flex: 1, padding: '14px', backgroundColor: COLORS.primary, color: 'white', borderRadius: '12px', border: 'none', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', fontFamily: FONT_FAMILY }}>
              ابدأ محادثة جديدة
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── EXPIRED STEP ──
  if (step === 'expired') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: FONT_FAMILY }} dir="rtl">
        <div style={{ backgroundColor: 'white', borderRadius: '24px', padding: '40px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: COLORS.warningLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <Clock style={{ width: '40px', height: '40px', color: COLORS.warning }} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '12px', fontFamily: FONT_FAMILY }}>
            انتهت صلاحية الخطة
          </h2>
          <p style={{ fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6, marginBottom: '24px', fontFamily: FONT_FAMILY }}>
            خطتك انتهت صلاحيتها بعد مرور 7 أيام. ابدأ محادثة جديدة مع هلا للحصول على خطة دعم محدثة.
          </p>
          <button onClick={handleBackToHome} data-testid="start-new-chat-expired-btn" style={{ width: '100%', padding: '16px', backgroundColor: COLORS.primary, color: 'white', borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
            ابدأ محادثة جديدة
          </button>
        </div>
      </div>
    );
  }

  // ── RESULT STEP ──
  if (step === 'result' && planData) {
    const { services = [], diagnosis = {}, progress_percent = 0, days_remaining = 0, completed_count = 0, services_count = 0 } = planData;

    return (
      <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg, fontFamily: FONT_FAMILY }} dir="rtl">
        {/* Header */}
        <header style={{ backgroundColor: COLORS.secondary, padding: '20px 24px', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <HalaAvatar state={AVATAR_STATES.SUCCESS} size={48} showStatusDot={false} />
                <div>
                  <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0, fontFamily: FONT_FAMILY }}>
                    خطة الدعم الخاصة بك
                  </h1>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', margin: 0, fontFamily: FONT_FAMILY }}>
                    {planData.client_name || 'عزيزي العميل'}
                  </p>
                </div>
              </div>
              <button onClick={handleBackToHome} style={{ padding: '8px 14px', backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: '8px', border: 'none', fontSize: '12px', cursor: 'pointer', fontFamily: FONT_FAMILY }}>
                الرئيسية
              </button>
            </div>
            
            {/* Progress Bar */}
            <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
              <div style={{ width: `${progress_percent}%`, height: '100%', backgroundColor: COLORS.primary, borderRadius: '10px', transition: 'width 0.5s ease' }}></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', fontFamily: FONT_FAMILY }}>
                {completed_count} من {services_count} مكتمل ({progress_percent}%)
              </span>
              <span style={{ fontSize: '11px', color: days_remaining <= 2 ? COLORS.warning : 'rgba(255,255,255,0.6)', fontFamily: FONT_FAMILY }}>
                متبقي {days_remaining} أيام
              </span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>
          {/* Diagnosis Summary */}
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '16px', marginBottom: '20px', border: `1px solid ${COLORS.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Target style={{ width: '18px', height: '18px', color: COLORS.secondary }} />
              <h2 style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>ملخص احتياجك</h2>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[
                { label: 'المشروع', value: diagnosis?.project_stage },
                { label: 'التحدي', value: diagnosis?.main_problem },
                { label: 'الدعم', value: diagnosis?.support_type },
                { label: 'المدينة', value: diagnosis?.city },
              ].filter(item => item.value).map(item => (
                <span key={item.label} style={{ fontSize: '12px', color: COLORS.textMuted, backgroundColor: COLORS.bgGray, padding: '6px 12px', borderRadius: '20px', fontFamily: FONT_FAMILY }}>
                  <strong style={{ color: COLORS.secondary }}>{item.value}</strong>
                </span>
              ))}
            </div>
          </div>

          {/* Services Grouped by Program */}
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.secondary, marginBottom: '16px', fontFamily: FONT_FAMILY }}>
              خدمات خطتك ({services_count})
            </h2>
            
            {(() => {
              const PROGRAM_ICONS = {
                briefcase: <FileText style={{ width: '16px', height: '16px' }} />,
                building: <Building2 style={{ width: '16px', height: '16px' }} />,
                graduation: <GraduationCap style={{ width: '16px', height: '16px' }} />,
                store: <Store style={{ width: '16px', height: '16px' }} />,
                rocket: <Rocket style={{ width: '16px', height: '16px' }} />,
              };

              // Group by program
              const groups = {};
              services.forEach(svc => {
                const pid = svc.program_id || 'other';
                if (!groups[pid]) {
                  groups[pid] = {
                    name: svc.program_name_ar || 'خدمات أخرى',
                    color: svc.program_color || '#64748B',
                    icon: svc.program_icon || '',
                    items: [],
                  };
                }
                groups[pid].items.push(svc);
              });

              return Object.entries(groups).map(([pid, group]) => (
                <div key={pid} style={{ marginBottom: '20px' }}>
                  {/* Program Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    marginBottom: '12px', padding: '10px 14px',
                    backgroundColor: `${group.color}12`,
                    borderRadius: '12px',
                    borderRight: `3px solid ${group.color}`,
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      backgroundColor: group.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', flexShrink: 0,
                    }}>
                      {PROGRAM_ICONS[group.icon] || <Star style={{ width: '16px', height: '16px' }} />}
                    </div>
                    <div>
                      <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>
                        خدمات {group.name}
                      </h3>
                      <span style={{ fontSize: '11px', color: COLORS.textMuted, fontFamily: FONT_FAMILY }}>
                        {group.items.length} {group.items.length === 1 ? 'خدمة' : 'خدمات'}
                      </span>
                    </div>
                  </div>

                  {/* Services in program */}
                  {group.items.map((service) => {
                    const status = service.status || 'NOT_STARTED';
                    const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED;
                    const StatusIcon = statusConfig.icon;

                    return (
                      <div key={service.service_id} style={{
                        backgroundColor: status === 'COMPLETED' ? COLORS.primaryLight : 'white',
                        borderRadius: '12px', padding: '14px',
                        border: `1px solid ${status === 'COMPLETED' ? COLORS.primary : status === 'IN_PROGRESS' ? COLORS.gold : COLORS.border}`,
                        marginBottom: '8px', marginRight: '10px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.secondary, margin: 0, fontFamily: FONT_FAMILY }}>
                            {service.service_display_name || service.service_name_ar}
                          </h4>
                          <span style={{ fontSize: '10px', fontWeight: 600, color: statusConfig.color, backgroundColor: statusConfig.bgColor, padding: '3px 8px', borderRadius: '20px', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <StatusIcon style={{ width: '10px', height: '10px' }} />
                            {statusConfig.label}
                          </span>
                        </div>
                        {service.delivery_mode_label && (
                          <span style={{
                            fontSize: '10px', fontWeight: 500,
                            color: service.delivery_mode === 'online' ? '#3B82F6' : service.delivery_mode === 'in_person' ? '#F59E0B' : '#8B5CF6',
                            backgroundColor: service.delivery_mode === 'online' ? '#EFF6FF' : service.delivery_mode === 'in_person' ? '#FFFBEB' : '#F5F3FF',
                            padding: '2px 8px', borderRadius: '10px', fontFamily: FONT_FAMILY,
                            display: 'inline-block', marginBottom: '4px',
                          }}>
                            {service.delivery_mode_label}
                          </span>
                        )}
                        <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0, lineHeight: 1.4, fontFamily: FONT_FAMILY }}>
                          {service.short_description_ar || 'خدمة دعم متخصصة'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>

          {/* Disclaimer */}
          <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '12px', padding: '14px 16px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Shield style={{ width: '18px', height: '18px', color: '#D97706', flexShrink: 0, marginTop: '2px' }} />
              <p style={{ fontSize: '12px', color: '#78350F', margin: 0, lineHeight: 1.6, fontFamily: FONT_FAMILY }}>
                <strong>إخلاء مسؤولية:</strong> هذه الخطة إرشادية فقط ولا تمثل التزاماً قانونياً أو مالياً.
              </p>
            </div>
          </div>

          {/* Action Button - Navigate to chat with plan auto-load */}
          <button
            onClick={handleContinuePlan}
            data-testid="continue-journey-btn"
            style={{
              width: '100%', padding: '16px', backgroundColor: COLORS.primary, color: 'white',
              borderRadius: '12px', border: 'none', fontSize: '16px', fontWeight: 'bold',
              cursor: 'pointer', fontFamily: FONT_FAMILY, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '10px',
            }}
          >
            أكمل خطتك مع هلا
            <ArrowRight style={{ width: '20px', height: '20px', transform: 'rotate(180deg)' }} />
          </button>
        </main>
      </div>
    );
  }

  // Fallback to search
  return null;
}
